import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Sparkles, Lightbulb, Layers, Brain, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import personasData from '@/data/personas.json';

interface Props {
    guidedStudy: GuidedStudy;
    currentStep: GuidedStudyStep;
    currentStepIndex: number;
    currentSubStepIndex: number;
    totalSubSteps: number;
    isCompleted: boolean;
    direction: 'forward' | 'backward';
    showFollowupPrompt: boolean;
    showHint: boolean;
    setShowHint: (show: boolean) => void;
    hasViewedCapability: boolean;
    userInput: string;
    setUserInput: (input: string) => void;
    finalReflection: string;
    setFinalReflection: (reflection: string) => void;
    saveTextResponse: (stepIndex: number, subStepIndex: number, text: string) => Promise<void>;
    handleCapabilityAction: (itemTitle?: string, overrideRef?: string, overrideItems?: CapabilityItemReference[]) => void;
    onClose: () => void;
    isDarkMode: boolean;
    methodInfo: { methodTitle?: string; methodSummary?: string; stepTitle?: string; stepDescription?: string } | null;
    subStepContent: {
        type: 'question' | 'capabilityItem' | 'methodStep' | 'step';
        content: any;
    };
};


export function GuidedStudyStepContent({
    guidedStudy,
    currentStep,
    currentStepIndex,
    currentSubStepIndex,
    totalSubSteps,
    isCompleted,
    direction,
    showFollowupPrompt,
    showHint,
    setShowHint,
    hasViewedCapability,
    userInput,
    setUserInput,
    finalReflection,
    setFinalReflection,
    saveTextResponse,
    handleCapabilityAction,
    onClose,
    isDarkMode,
    methodInfo,
    subStepContent,
}: Props) {
    const saveTimerRef = useRef<NodeJS.Timeout | null>(null);

    return (
        <>
            {/* Study Hook - Show clearly at the start of the study, below progress bar */}
            <AnimatePresence>
                {currentStepIndex === 0 && currentSubStepIndex === 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        className="py-2.5 px-3 bg-primary/5 border-l-4 border-primary rounded-r-md overflow-hidden relative mb-4"
                    >
                        <p className="text-sm font-medium italic text-foreground/90 leading-relaxed">
                            "{guidedStudy.hook}"
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Completion State */}
            {isCompleted ? (
                <div className="flex flex-col items-center justify-center py-8 space-y-6">
                    <div className="relative">
                        <CheckCircle2 className="h-16 w-16 text-green-500/80" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="absolute -top-1 -right-1"
                        >
                            <Sparkles className="h-6 w-6 text-amber-400" />
                        </motion.div>
                    </div>

                    <div className="text-center space-y-3 max-w-md px-4">
                        <h3 className="text-2xl font-bold tracking-tight">Journey Complete</h3>
                        <p className="text-muted-foreground leading-relaxed">
                            You've walked through this passage through the lens of <span className="text-foreground font-semibold">the {personasData.personas.find(p => p.id === guidedStudy.personaId)?.title || 'Explorer'}</span>.
                        </p>
                    </div>

                    {/* Final Reflection Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className={`w-full max-w-md p-5 rounded-xl border ${isDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-slate-50 border-slate-200'} space-y-4`}
                    >
                        <div className="space-y-1.5">
                            <h4 className="text-sm font-bold uppercase tracking-wider text-primary/80 flex items-center gap-2">
                                <Lightbulb className="h-4 w-4" />
                                Final Reflection
                            </h4>
                            <p className="text-sm font-medium leading-relaxed">
                                {(() => {
                                    const persona = personasData.personas.find(p => p.id === guidedStudy.personaId);
                                    if (persona?.id === 'daily-disciple') return "How does this deeper understanding change your walk with God today? What's one specific way you can obey or trust Him differently?";
                                    if (persona?.id === 'serious-student') return "Beyond the exegesis, how has the weight of this text settled on your heart? Transform your study into a moment of worship.";
                                    if (persona?.id === 'curious-skeptic') return "Looking at the grit and reality of this ancient context, what claim of the text challenges your perspective the most?";
                                    if (persona?.id === 'context-detective') return "Now that the 'Aha!' moments have clicked, what is the spiritual core that these facts were pointing to?";
                                    if (persona?.id === 'new-believer') return "This is part of your new foundation. What's the one truth from this study that you want to hang onto as you grow?";
                                    return "Take a moment to let these truths sink in. What is your biggest takeaway from this encounter with the text?";
                                })()}
                            </p>
                        </div>

                        <div className="space-y-3">
                            <Textarea
                                placeholder="Write down your final thoughts..."
                                className="min-h-32"
                                value={finalReflection}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFinalReflection(val);
                                    saveTextResponse(999, 999, val);
                                }}
                            />
                            <Button onClick={onClose} className="w-full">Close Study</Button>
                        </div>
                    </motion.div>
                </div>
            ) : (
                <AnimatePresence mode="wait">
                    <motion.div
                        key={`${currentStepIndex}-${currentSubStepIndex}`}
                        initial={{ opacity: 0, x: direction === 'forward' ? 10 : -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: direction === 'forward' ? -10 : 10 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="pt-2"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold tracking-tight">{currentStep.title}</h3>
                            {currentStep.methodRef && (
                                <div className="flex items-center gap-1.5">
                                    <span className="opacity-40">•</span>
                                    <Badge variant="outline" className="text-[9px] uppercase tracking-wide font-semibold text-muted-foreground py-0 h-4 border-muted-foreground/20">
                                        {methodInfo?.methodTitle}
                                    </Badge>
                                </div>
                            )}
                        </div>

                        <p className="text-sm font-medium text-foreground/80 leading-relaxed mb-5">
                            {currentStep.instruction}
                        </p>

                        <AnimatePresence>
                            {showFollowupPrompt && (currentStep.followupPrompt || subStepContent.content?.followupPrompt) && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                    animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    className={`mb-5 p-5 rounded-xl border relative overflow-hidden backdrop-blur-md shadow-lg ${isDarkMode
                                        ? 'bg-amber-950/40 border-amber-900/50 text-amber-50'
                                        : 'bg-amber-50/80 border-amber-200 text-amber-900'
                                        }`}
                                >
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div className={`p-1 rounded-md ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                                            <Sparkles className={`h-3.5 w-3.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'} animate-pulse`} />
                                        </div>
                                        <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${isDarkMode ? 'text-amber-400/90' : 'text-amber-700'}`}>
                                            Deeper Discovery
                                        </span>
                                    </div>
                                    <p className="text-[15px] font-medium leading-relaxed relative z-10 antialiased italic">
                                        "{currentStep.followupPrompt || subStepContent.content?.followupPrompt}"
                                    </p>
                                    {/* Decorative background element */}
                                    <div className={`absolute -bottom-2 -right-2 p-1 opacity-[0.03] scale-[2.5] rotate-12 ${isDarkMode ? 'text-amber-400' : 'text-amber-900'}`}>
                                        <Brain className="h-12 w-12" />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {showHint && (currentStep as any).hint && (
                            <motion.div
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`p-3 rounded-lg border mb-5 text-[13px] leading-relaxed italic ${isDarkMode ? 'bg-amber-900/10 border-amber-900/30 text-amber-200/70' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                            >
                                <div className={`flex items-center gap-1.5 mb-1 text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-amber-500' : 'text-amber-600'}`}>
                                    <Lightbulb className="h-3 w-3" />
                                    Expert Guidance
                                </div>
                                {(currentStep as any).hint}
                            </motion.div>
                        )}

                        {/* Response Area */}
                        {subStepContent.type === 'question' && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-0.5">
                                        {subStepContent.content?.question}
                                    </label>
                                    <Textarea
                                        placeholder="Type your reflection here..."
                                        value={userInput}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setUserInput(newValue);

                                            // Clear existing timer
                                            if (saveTimerRef.current) {
                                                clearTimeout(saveTimerRef.current);
                                            }

                                            // Auto-save after user stops typing for 1 second (debounced)
                                            saveTimerRef.current = setTimeout(() => {
                                                saveTextResponse(currentStepIndex, currentSubStepIndex, newValue);
                                            }, 1000);
                                        }}
                                        className="min-h-32"
                                    />
                                    {(currentStep as any).hint && (
                                        <div className="flex items-center justify-between">
                                            <p className="text-[10px] text-muted-foreground italic">
                                                Your progress is automatically saved
                                            </p>
                                            {!showHint && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setShowHint(true)}
                                                    className="h-6 px-2 text-[10px] uppercase font-bold text-primary hover:bg-primary/10"
                                                >
                                                    Stuck? Get a lens hint
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Capability Items as clickable buttons */}
                        {subStepContent.type === 'capabilityItem' && subStepContent.content && (
                            <div className="space-y-3">
                                {totalSubSteps > 1 && (
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">
                                        Item {currentSubStepIndex + 1} of {totalSubSteps}:
                                    </p>
                                )}
                                <Button
                                    variant="default"
                                    size="default"
                                    className={cn(
                                        "text-left justify-start h-auto py-3 px-4 gap-3 relative overflow-hidden transition-all",
                                        !hasViewedCapability && "ring-2 ring-primary ring-offset-2 shadow-lg"
                                    )}
                                    onClick={() => handleCapabilityAction(subStepContent.content?.itemTitle)}
                                >
                                    <Layers className="h-5 w-5 flex-shrink-0" />
                                    <div className="flex flex-col items-start gap-0.5 relative z-10">
                                        {currentStep.buttonText && (
                                            <span className="font-medium">{currentStep.buttonText}</span>
                                        )}
                                        <span className="font-normal text-xs">{subStepContent.content.itemTitle}</span>
                                        {subStepContent.content.relevanceNote && (
                                            <span className="text-xs opacity-80 font-normal">{subStepContent.content.relevanceNote}</span>
                                        )}
                                    </div>
                                </Button>
                                {!hasViewedCapability && (
                                    <p className="text-[10px] font-bold text-primary uppercase tracking-widest animate-pulse px-1">
                                        Click to view instructions
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Method Steps (from engagement methods) */}
                        {subStepContent.type === 'methodStep' && subStepContent.content && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide font-bold py-0.5 h-auto">
                                        Step {currentSubStepIndex + 1}
                                    </Badge>
                                    <h4 className="font-bold text-sm tracking-tight">{(subStepContent.content as any).title}</h4>
                                </div>
                                <div className={`prose prose-sm max-w-none text-foreground/80 leading-relaxed italic border-l-2 pl-4 border-primary/20 ${isDarkMode ? 'prose-invert' : ''}`}>
                                    <ReactMarkdown>{String((subStepContent.content as any).instruction || (subStepContent.content as any).description || '')}</ReactMarkdown>
                                </div>

                                {/* If the method step has a capabilityRef, show the button */}
                                {(subStepContent.content as any).capabilityRef && (
                                    <div className="pt-2">
                                        <Button
                                            variant="default"
                                            onClick={() => handleCapabilityAction(undefined, (subStepContent.content as any).capabilityRef)}
                                            className={cn("gap-2", !hasViewedCapability && "ring-2 ring-primary ring-offset-2 shadow-lg")}
                                        >
                                            <Layers className="h-4 w-4" />
                                            {currentStep.buttonText || 'Begin Interaction'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Fallback Text Input (if interactionMode is 'text' but no sub-questions) */}
                        {currentStep.interactionMode === 'text' && subStepContent.type !== 'question' && (
                            <div className="mt-4">
                                <Textarea
                                    placeholder="Type your response here..."
                                    value={userInput}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setUserInput(newValue);

                                        // Clear existing timer
                                        if (saveTimerRef.current) {
                                            clearTimeout(saveTimerRef.current);
                                        }

                                        // Auto-save after user stops typing for 1 second (debounced)
                                        saveTimerRef.current = setTimeout(() => {
                                            saveTextResponse(currentStepIndex, currentSubStepIndex, newValue);
                                        }, 1000);
                                    }}
                                    className="min-h-32"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Your response is automatically saved
                                </p>
                            </div>
                        )}

                        {/* Capability Reference Button (for steps with capabilityRef but NOT using capabilityItems as sub-steps) */}
                        {subStepContent.type !== 'capabilityItem' && currentStep.capabilityRef && (
                            <div className="mt-4">
                                <Button
                                    variant="default"
                                    size="default"
                                    className={cn(
                                        "gap-2",
                                        !hasViewedCapability && "ring-2 ring-primary ring-offset-2 shadow-lg"
                                    )}
                                    onClick={() => handleCapabilityAction()}
                                >
                                    <Layers className="h-4 w-4" />
                                    {currentStep.buttonText || 'Open Marking Tools'}
                                </Button>
                                {!hasViewedCapability && (
                                    <p className="text-[10px] font-bold text-primary uppercase tracking-widest animate-pulse px-1 mt-2">
                                        Click to begin
                                    </p>
                                )}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            )}

        </>
    );
}
